import { TextField, Typography } from "@material-ui/core"
import { useRef, useState } from "react"

// Realça (fundo amarelo) todas as ocorrências de `termo` dentro de `texto`,
// devolvendo nós React. Sem termo/ocorrência, devolve o texto puro. Usado para
// destacar o termo pesquisado nos nomes (categoria/produto/opção/passo).
export function destacarTermo(texto, termo) {
    const s = String(texto ?? "")
    const t = String(termo ?? "").trim()
    if (!t) return s
    const alvo = t.toLowerCase()
    const base = s.toLowerCase()
    if (!base.includes(alvo)) return s
    const partes = []
    let i = 0
    let chave = 0
    while (i < s.length) {
        const j = base.indexOf(alvo, i)
        if (j === -1) {
            partes.push(s.slice(i))
            break
        }
        if (j > i) partes.push(s.slice(i, j))
        partes.push(
            <mark
                key={chave++}
                style={{ backgroundColor: "#fff59d", padding: 0, borderRadius: 2 }}
            >
                {s.slice(j, j + t.length)}
            </mark>
        )
        i = j + t.length
    }
    return partes
}

// Texto que fica em modo leitura e vira campo ao clicar (click-to-edit).
// Ao confirmar (blur ou Enter em campo simples) grava via onCommit; Esc cancela.
// `formatarView` formata o texto exibido em leitura; `inputType`/`inputProps`/
// `inputClassName` customizam o campo (ex.: preço numérico alinhado à direita).
// Compartilhado entre a edição inline de produto (ItemProduto) e de opção de
// produto (ItemAdicional), para que os dois se comportem igual.
export function TextoEditavel({
    valorInicial,
    onCommit,
    multiline,
    placeholder,
    viewClassName,
    formatarView,
    inputType,
    inputProps,
    inputClassName,
    ativo,
    onAtivar,
    onFechar,
    onNavegar,
    termoDestaque,
}) {
    const [valor, setValor] = useState(valorInicial ?? "")
    const inputRef = useRef(null)
    // Evita que o onBlur disparado ao trocar de campo (Tab) feche a edição e
    // sobrescreva o campo que acabou de ser ativado.
    const navegandoRef = useRef(false)

    const commit = () => {
        const novo = inputRef.current?.value ?? ""
        if (String(novo) !== String(valor)) {
            setValor(novo)
            onCommit(novo)
        }
    }

    if (!ativo) {
        const vazio = valor === "" || valor === null || valor === undefined
        const texto = vazio
            ? placeholder
            : formatarView
              ? formatarView(valor)
              : termoDestaque
                ? destacarTermo(valor, termoDestaque)
                : valor
        return (
            <Typography
                className={viewClassName}
                onClick={onAtivar}
                title="Clique para editar"
                style={{
                    cursor: "text",
                    ...(vazio ? { fontStyle: "italic", opacity: 0.6 } : {}),
                }}
            >
                {texto}
            </Typography>
        )
    }

    return (
        <TextField
            fullWidth
            autoFocus
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            type={inputType}
            className={inputClassName}
            inputProps={inputProps}
            variant="outlined"
            size="small"
            defaultValue={valor}
            inputRef={inputRef}
            onBlur={() => {
                if (navegandoRef.current) {
                    navegandoRef.current = false
                    return
                }
                commit()
                onFechar && onFechar()
            }}
            onKeyDown={e => {
                if (e.key === "Enter" && !multiline) {
                    e.preventDefault()
                    commit()
                    onFechar && onFechar()
                } else if (e.key === "Escape") {
                    onFechar && onFechar()
                } else if (e.key === "Tab") {
                    // Tab (ou Shift+Tab) confirma e ativa a edição do campo seguinte/anterior.
                    e.preventDefault()
                    navegandoRef.current = true
                    commit()
                    onNavegar && onNavegar(e.shiftKey ? -1 : 1)
                }
            }}
        />
    )
}

// Formata número para moeda BRL; vazio/NaN → string vazia (cai no placeholder).
export function formatarPreco(v) {
    const n = parseFloat(String(v).replace(",", "."))
    return isNaN(n) ? "" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}
